import {
  DefaultAlertDialog,
  DefaultSpinner,
  LabeledText,
} from '~/common/components/defaultUIComponents';
import {
  ChangeRequest,
  ChangeRequestType,
} from '~/common/entities/ChangeRequest/ChangeRequestType';
import ChangeRequestApi from '~/common/api/zeiterfassungServerApi/ChangeRequestApi';
import EmployeeApi from '~/common/api/zeiterfassungServerApi/EmployeeApi';
import ProjectApi from '~/common/api/zeiterfassungServerApi/ProjectApi';
import { JobStepNumberDetails } from './JobStepNumberDetails';
import { CopyToProjectDetails } from './CopyToProjectDetails';
import { CombineStepsDetails } from './CombineStepsDetails';
import { useDisclosure, Stack } from '@chakra-ui/react';
import useTranslate from '~/common/utils/translate';
import { CircularProgress } from '@chakra-ui/react';
import { Paper } from '~/common/components/Paper';
import { DeleteDetails } from './DeleteDetails';
import React, { useState } from 'react';

type ChangeRequestTileProps = {
  changeRequest: ChangeRequest;
};

export const ChangeRequestTile: React.FC<ChangeRequestTileProps> = ({
  changeRequest,
}) => {
  const { mutateAsync: deleteChangeRequest, isLoading: isDeleting } =
    ChangeRequestApi.hooks.useDeleteChangeRequestMutation();
  const [{ data: projectOfJob, isLoading: isLoadingProject }] =
    ProjectApi.hooks.useProjectQuery(
      { id: changeRequest.job?.projects[0] },
      { enabled: !!changeRequest.job?.projects[0] },
    );
  const [openCollapse, setOpenCollapse] = useState<boolean>(false);
  const translate = useTranslate();

  const { data: employeeName = { firstName: '', lastName: '' } } =
    EmployeeApi.hooks.useEmployeeName(changeRequest.employee.id);

  const {
    isOpen: openDeleteDialog,
    onClose: closeDeleteDialog,
    onToggle: toggleOpenDeleteDialog,
  } = useDisclosure();

  const onDeleteModalAction = () => {
    closeDeleteDialog();
    void deleteChangeRequest(changeRequest);
  };

  if (!changeRequest.job || isDeleting) {
    return isDeleting ? <DefaultSpinner /> : null;
  }
  if (isLoadingProject) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <CircularProgress isIndeterminate color="blue.300" />
      </div>
    );
  }

  return (
    <>
      <Paper>
        <DefaultAlertDialog
          dialogHeaderText="changeRequest.delete.modal.header"
          isOpen={openDeleteDialog}
          onConfirmedAction={onDeleteModalAction}
          onCancel={toggleOpenDeleteDialog}
          dialogBodyText=""
        />
        <Stack>
          <LabeledText label="changeRequest.project">
            {projectOfJob?.name}
          </LabeledText>
          <LabeledText label="changeRequest.stepNumber">
            {changeRequest.job.step_number}
          </LabeledText>
          <LabeledText label="changeRequest.job">
            {changeRequest.job.name}
          </LabeledText>
          <LabeledText label="changeRequest.employee">
            {`${employeeName!.firstName} ${employeeName!.lastName}`}
          </LabeledText>
          <LabeledText label="changeRequest.type">
            {translate(`changeRequest.type.${changeRequest.type}`)}{' '}
          </LabeledText>

          {changeRequest.type === ChangeRequestType.JOB_STEP_NUMBER && (
            <JobStepNumberDetails
              changeRequest={changeRequest}
              openCollapse={openCollapse}
              setOpenCollapse={setOpenCollapse}
              toggleOpenDeleteDialog={toggleOpenDeleteDialog}
              projectOfJob={projectOfJob!}
            />
          )}

          {changeRequest.type === ChangeRequestType.DELETE && (
            <DeleteDetails
              changeRequest={changeRequest}
              openCollapse={openCollapse}
              setOpenCollapse={setOpenCollapse}
              toggleOpenDeleteDialog={toggleOpenDeleteDialog}
              projectOfJob={projectOfJob!}
            />
          )}

          {changeRequest.type === ChangeRequestType.COPY_TO_PROJECT && (
            <CopyToProjectDetails
              changeRequest={changeRequest}
              openCollapse={openCollapse}
              setOpenCollapse={setOpenCollapse}
              toggleOpenDeleteDialog={toggleOpenDeleteDialog}
              projectOfJob={projectOfJob!}
            />
          )}

          {changeRequest.type === ChangeRequestType.COMBINE_STEPS && (
            <CombineStepsDetails
              changeRequest={changeRequest}
              openCollapse={openCollapse}
              setOpenCollapse={setOpenCollapse}
              toggleOpenDeleteDialog={toggleOpenDeleteDialog}
              projectOfJob={projectOfJob!}
            />
          )}
        </Stack>
      </Paper>
    </>
  );
};
